import { Component } from '@theme/component';

/**
 * IG attribution banner (sections/wd-ig-attribution.liquid).
 *
 * The section renders hidden with the ad_partnerships metaobject entries embedded as JSON, and an
 * inline parse-time script in the section reveals it pre-paint when utm_term matches a partner.
 * This module fills in the content: avatar, @handle, subheading (founder vs partner template)
 * and the paid-partnership pill. No match → the banner stays hidden and nothing else changes,
 * same fail-open contract as the bundle-builder URL prefill.
 *
 * In the theme editor (data-design-mode) there is no utm_term, so the first partner is shown
 * as sample content to make the section styleable.
 *
 * @extends {Component}
 */

const normalizeHandle = (value) =>
  String(value || '')
    .trim()
    .replace(/^@/, '')
    .toLowerCase();

// utm_term is a '__'-separated segment string (AF_..., CN_..., CR_Alex L, O_..., ...).
// An entry's match key (its utm_match field) lists one or more of those segments verbatim.
const segmentsOf = (value) =>
  String(value || '')
    .split('__')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

class WdIgAttribution extends Component {
  requiredRefs = ['avatar', 'handle', 'subheading', 'pill'];

  connectedCallback() {
    super.connectedCallback();

    const partners = this.#readPartners();
    // The utm_term persists for the tab session, scoped to the path it was first seen on, so
    // a visitor who browses away and returns to this page gets the strip again. Latest term
    // wins. Key shared with the ad content spots (edit-spots). Storage failures (private
    // mode) fall back to URL-only behaviour.
    let term = null;
    try {
      term = new URLSearchParams(window.location.search).get('utm_term');
    } catch (e) {
      /* no query string = no banner */
    }
    try {
      if (term) {
        sessionStorage.setItem('wd_ad_utm', JSON.stringify({ t: term, p: window.location.pathname }));
      } else {
        const stored = JSON.parse(sessionStorage.getItem('wd_ad_utm') || 'null');
        if (stored && stored.p === window.location.pathname) term = stored.t;
      }
    } catch (e) {
      /* storage unavailable */
    }

    // An entry matches when every segment it lists appears in the visitor's utm_term; when
    // several entries match (a generic creator entry and an offer-specific one), the most
    // specific — the one listing the most segments — wins. Same rule as the ad content spots.
    const visitorSegments = new Set(segmentsOf(term));
    let partner = null;
    let bestCount = 0;
    for (const candidate of partners) {
      const required = segmentsOf(candidate && candidate.match);
      if (!required.length) continue;
      if (!required.every((segment) => visitorSegments.has(segment))) continue;
      if (required.length > bestCount) {
        bestCount = required.length;
        partner = candidate;
      }
    }
    // The winning entry can opt out of the handle strip (show_instagram_handle = false): its
    // visitors get the ad content spots only. Ignored in the theme editor, where the strip
    // must stay visible to be styleable.
    if (partner && partner.show === false && this.dataset.designMode !== 'true') {
      document.documentElement.classList.remove('wd-ig-attr-show');
      return;
    }
    if (!partner && this.dataset.designMode === 'true') {
      partner = partners[0] || null;
    }

    if (!partner) {
      // Keep the belt on the pre-paint gate: never leave an empty shell visible.
      document.documentElement.classList.remove('wd-ig-attr-show');
      return;
    }

    const name = String(partner.name || '').trim();
    const isFounder = partner.founder === true;
    const template = isFounder ? this.dataset.founderText : this.dataset.partnerText;

    this.refs.handle.textContent = '@' + normalizeHandle(partner.handle);
    this.refs.subheading.textContent = String(template || '').split('[name]').join(name);
    if (partner.image) {
      this.refs.avatar.src = partner.image;
      this.refs.avatar.alt = name;
    }
    this.refs.pill.hidden = isFounder;

    // Covers the theme editor and any case where the inline gate could not run.
    document.documentElement.classList.add('wd-ig-attr-show');
  }

  /** @returns {Array<{match: string|null, show: boolean, handle: string, name: string, image: string|null, founder: boolean}>} */
  #readPartners() {
    try {
      const data = JSON.parse(document.getElementById(this.dataset.configId).textContent);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }
}

if (!customElements.get('wd-ig-attribution')) {
  customElements.define('wd-ig-attribution', WdIgAttribution);
}
